import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import {
    AttachmentServiceProxy,
    BreadcrumbRequestInput,
    CommonAttachment,
    FavoriteServiceProxy,
    FileAttachmentRefInput,
    OneDriveServiceProxy
} from '@shared/service-proxies/service-proxies';

/**
 * The Graph coordinates a row needs before a trail can be resolved for it. Declared structurally so any
 * favorites row satisfies it without being cast — the side menu's rows, the manager's, the add pool's.
 */
export interface BreadcrumbTarget {
    /** The drive the item lives in. Nothing resolves without it. */
    driveId?: string;
    siteId?: string;
    /** The item's containing folder, when the row was stored with one. */
    folderId?: string;
    /** The item's own Graph id — TrAttachments.KeyId — used when there is no folder to walk up from. */
    keyId?: string;
}

/** One resolvable item, keyed so a result can be matched back without re-deriving anything. */
interface TrailRequest {
    key: string;
    driveId: string;
    itemId: string;
    siteId: string;
    /** True when the walk starts from the file itself, so its own node has to come off the end. */
    fromFile: boolean;
}

/**
 * The parts of favoriting that every surface needs and none of them should own: where a favorited file
 * lives, and which attachment row a browsed cloud file should be pinned through.
 *
 * Both answers have to be the same wherever they are asked — the side menu, the favorites manager, the
 * add pool, the Files tabs — which is what a single service buys over a copy per component.
 */
@Injectable({ providedIn: 'root' })
export class FavoriteService {
    /** Resolved trails, keyed drive|item. */
    private readonly trailByItem = new Map<string, string[]>();

    /** Items with a request in flight, so two callers don't both ask for the same one. */
    private readonly pending = new Set<string>();

    /**
     * Items GetBreadcrumbs answered without a usable trail — a file whose drive item is gone, one the
     * user can no longer read, or a lookup made before the Graph token was ready.
     *
     * Remembered because they cannot go in `trailByItem`: with nothing to cache, every later call asked
     * for them again, and each answer counted as "something was fetched", which re-entered the
     * rebuild -> resolve -> rebuild cycle in the favorites manager and fired GetBreadcrumbs in a loop
     * for as long as the panel stayed open. Cleared by `retryUnresolved`, so a token that arrives late
     * still gets a second chance on the next deliberate attempt rather than on every render.
     */
    private readonly unresolved = new Set<string>();

    constructor(
        private readonly _oneDriveService: OneDriveServiceProxy,
        private readonly _attachmentService: AttachmentServiceProxy,
        private readonly _favoriteService: FavoriteServiceProxy
    ) { }

    /**
     * The TrAttachments id to favorite for a browsed cloud file: the row it already has, or a new one.
     *
     * Attachments are shared (Entity = AllUserIncludeMe) rather than minted per add, so a file already
     * recorded is pinned through the row it has instead of collecting a second — which is what
     * CreateAttachment did on every click, since it only ever inserts.
     *
     * The check asks whether a row EXISTS, not whether the caller favorited it: pins come and go, so
     * unfavoriting and re-favoriting the same file must not mint a second attachment for it.
     *
     * Identified by drive AND item id — a Graph item id is unique only within its own drive. A file with
     * no Graph id at all, such as a typed web link, skips the check and goes straight to creating its row.
     *
     * Emits 0 when the row could neither be found nor created, which callers report as a failed add.
     */
    attachmentIdForFile(attachment: CommonAttachment): Observable<number> {
        const create = () => this._attachmentService.createAttachment(attachment).pipe(
            map(created => (created.isError ? 0 : Number(created.responseData) || 0)));

        if (!attachment.keyId) {
            return create();
        }

        const wanted = new FileAttachmentRefInput();
        wanted.msFileId = attachment.keyId;
        wanted.msDriveId = attachment.msDriveId;

        return this._favoriteService.getFavoritedFileKeyIds([wanted]).pipe(
            switchMap(found => {
                const existing = !found.isError ? (found.responseData || [])[0] : null;
                return existing?.attachmentId ? of(Number(existing.attachmentId)) : create();
            }),
            // A failed check is not a reason to refuse the add — fall through to creating the row, which
            // is exactly what happened before the check existed.
            catchError(() => create()));
    }

    /**
     * Whether a trail can be resolved for this row at all. A project, client, app or web link carries no
     * drive, so this admits every file and folder without enumerating the extensions that count as one.
     */
    canResolve(target: BreadcrumbTarget): boolean {
        return this.requestFor(target) !== null;
    }

    /**
     * Forgets the items that came back without a trail, so the next `resolve` asks about them again.
     *
     * For a deliberate, user-initiated attempt — hovering a row for its tooltip, or reopening the
     * favorites panel — never on a render path: re-asking on every render is exactly the loop the
     * `unresolved` set exists to stop.
     */
    retryUnresolved(): void {
        this.unresolved.clear();
    }

    /** The resolved trail for a row, or null while one has yet to land. */
    trailOf(target: BreadcrumbTarget): string[] {
        const request = this.requestFor(target);
        return request ? this.trailByItem.get(request.key) || null : null;
    }

    /**
     * Where a favorited file or folder lives, from OneDrive/GetBreadcrumbs. Resolves whatever these rows
     * still need, in one call, and completes when the cache has been updated. Callers then read their rows
     * back through <see cref="trailOf"/>.
     *
     * A file's location is a Graph lookup rather than a column, so GetFavorites cannot carry it the way it
     * carries a project's client or a person's type.
     *
     * Walks up from the containing folder where the row has one. Where it does not — which is every row
     * stored without MsFolderId — it walks from the file's own id instead, the id MyDriveItemOutput
     * documents as "the ItemId a breadcrumb resolves", and drops the file's own node off the end: what is
     * wanted is where the file lives, not the file. Without that second path such rows resolve nothing at
     * all, since a drive id alone yields only the site and library.
     *
     * Answers are cached by item, not held on the rows, so a reload that rebuilds them keeps what already
     * resolved. A failed lookup is deliberately left uncached: the first pass often runs during app
     * bootstrap before the Graph token is ready, and asking again later is how that recovers.
     *
     * Safe to call repeatedly — on load and again when a tooltip opens, say. Emits true only when a trail
     * was actually LEARNED, so a caller may rebuild its list on it: the rows that resolved are cached and
     * drop out of the next pass, and the ones that did not are remembered as unresolved, so the sequence
     * terminates. Emitting "a request was sent" instead is what made the favorites manager loop —
     * unresolvable rows answered yes on every pass.
     */
    resolve(targets: BreadcrumbTarget[]): Observable<boolean> {
        const wanted = new Map<string, TrailRequest>();

        (targets || []).forEach(target => {
            const request = this.requestFor(target);

            if (!request
                || this.trailByItem.has(request.key)
                || this.unresolved.has(request.key)
                || this.pending.has(request.key)
                || wanted.has(request.key)) {
                return;
            }

            wanted.set(request.key, request);
        });

        if (wanted.size === 0) {
            return of(false);
        }

        const keys = Array.from(wanted.keys());
        keys.forEach(k => this.pending.add(k));

        const input = keys.map(k => {
            const request = wanted.get(k);
            const item = new BreadcrumbRequestInput();
            // The cache key itself, so a result needs no re-deriving to match.
            item.key = k;
            item.driveId = request.driveId;
            item.itemId = request.itemId;
            item.siteId = request.siteId;
            return item;
        });

        return this._oneDriveService.getBreadcrumbs(input).pipe(
            map(result => {
                let learned = 0;

                if (!result.isError && result.responseData) {
                    result.responseData.forEach(trail => {
                        const names = (trail.breadcrumb || [])
                            .map(b => (b.name || '').trim())
                            .filter(name => name.length > 0);

                        // The last node is the file itself when the walk started from it.
                        if (wanted.get(trail.key)?.fromFile && names.length > 0) {
                            names.pop();
                        }

                        if (names.length > 0) {
                            this.trailByItem.set(trail.key, names);
                            learned++;
                        }
                    });
                }

                keys.forEach(k => this.pending.delete(k));

                // Anything asked for and still without a trail is not worth asking about again until
                // someone deliberately retries — see the `unresolved` field.
                keys.filter(k => !this.trailByItem.has(k)).forEach(k => this.unresolved.add(k));

                // Whether a trail was LEARNED, not whether a request went out. A caller that rebuilds
                // its list on this and then re-enters resolve() would otherwise never stop: rows that
                // resolve get cached and drop out, rows that do not kept answering "yes, we fetched".
                return learned > 0;
            }),
            catchError(() => {
                keys.forEach(k => this.pending.delete(k));
                // A transport failure is the case worth retrying — the first pass often runs during app
                // bootstrap, before the Graph token exists — so these keys are NOT marked unresolved.
                // Nothing was learned either way, so no caller rebuilds on it.
                return of(false);
            })
        );
    }

    /** What to ask GetBreadcrumbs for on this row's behalf, or null when there is nothing to ask. */
    private requestFor(target: BreadcrumbTarget): TrailRequest {
        if (!target?.driveId) {
            return null;
        }

        const fromFile = !target.folderId;
        const itemId = target.folderId || target.keyId;

        return itemId
            ? { key: `${target.driveId}|${itemId}`, driveId: target.driveId, itemId, siteId: target.siteId, fromFile }
            : null;
    }
}
